import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import SendIcon from '@mui/icons-material/Send';

const MAX_WORD_LENGTH = 20;
const VALID_WORD_RE = /^[a-zA-ZáéíóúÁÉÍÓÚàèìòùÀÈÌÒÙäëïöüÄËÏÖÜñÑçÇ]+$/;

const normalizeWord = (w) =>
  w.replace(/[áÁàÀäÄ]/g, 'a').replace(/[éÉèÈëË]/g, 'e')
    .replace(/[íÍìÌïÏ]/g, 'i').replace(/[óÓòÒöÖ]/g, 'o')
    .replace(/[úÚùÙüÜ]/g, 'u').replace(/[çÇ]/g, 'c')
    .replace(/[^a-zA-ZñÑ]/g, '').toUpperCase();

export default function WordSubmissionForm({ submittedWords, maxWords, onSubmit, disabled, bannedWords = [] }) {
  const { t } = useTranslation();
  const [word, setWord] = useState('');
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState('');
  const submittingRef = useRef(false);

  const validate = (value) => {
    const trimmed = value.trim();
    if (!trimmed) return 'home.wordEmpty';
    if (!VALID_WORD_RE.test(trimmed)) return 'home.wordInvalidChars';
    if (trimmed.length > MAX_WORD_LENGTH) return 'home.wordTooLong';
    const normalized = normalizeWord(trimmed);
    if (submittedWords.map((w) => normalizeWord(w.raw ?? w)).includes(normalized)) {
      return 'home.duplicateWord';
    }
    if (bannedWords.includes(normalized)) {
      return 'home.wordBanned';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submittingRef.current) return;

    const trimmed = word.trim();
    const validationKey = validate(trimmed);
    if (validationKey) {
      setLocalError(t(validationKey));
      return;
    }

    submittingRef.current = true;
    setLocalError('');
    setLoading(true);
    try {
      await onSubmit(trimmed);
      setWord('');
    } finally {
      setLoading(false);
      submittingRef.current = false;
    }
  };

  const progress = (submittedWords.length / maxWords) * 100;
  const remaining = maxWords - submittedWords.length;

  return (
    <Card sx={{ width: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            {t('home.wordsSubmitted', { count: submittedWords.length, max: maxWords })}
          </Typography>
          <Typography variant="caption" color={remaining <= 1 ? 'warning.main' : 'text.secondary'}>
            {remaining} restantes
          </Typography>
        </Box>

        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ mb: 2, borderRadius: 4, height: 6 }}
          color={progress >= 100 ? 'success' : 'primary'}
        />

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ display: 'flex', gap: 1 }}
          noValidate
        >
          <TextField
            value={word}
            onChange={(e) => {
              setWord(e.target.value);
              if (localError) setLocalError('');
            }}
            placeholder={t('home.wordPlaceholder')}
            disabled={disabled || loading}
            error={!!localError}
            helperText={localError}
            size="small"
            fullWidth
            inputProps={{ maxLength: MAX_WORD_LENGTH }}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
          />
          <Button
            type="submit"
            variant="contained"
            disabled={disabled || loading || !word.trim()}
            endIcon={<SendIcon />}
            sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            {t('home.submitWord')}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
